import React, { useState, useEffect, useRef } from "react";
import { Clock } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import { AuthorizationResult } from "../../models/AuthorizationResult";
import { cn } from "@/lib/utils";

const AccessTimer: React.FC<{
  hasAccess: boolean;
  isTemporary: boolean;
  endDate: Date | null;
}> = ({ endDate, hasAccess, isTemporary }) => {
  const navigate = useNavigate();
  const { refreshToken, login } = useAuth();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0,
  });

  const refreshCalled = useRef(false);

  useEffect(() => {
    if (!endDate || !isTemporary) return;

    const refreshAccess = async () => {
      if (refreshCalled.current) return;

      refreshCalled.current = true;
      try {
        const apiUrl = import.meta.env.VITE_API_URL ?? "/";
        const api = axios.create({ baseURL: apiUrl, timeout: 30000 });
        const response = await api.post(`api/RefreshToken`, {
          refreshToken: refreshToken,
        });
        const tokens = response.data as AuthorizationResult;
        login(tokens.accessToken, tokens.refreshToken);
      } catch (error) {
        alert("Session expired!");
        navigate("/login");
        console.error("Failed to refresh access token:", error);
      }
    };

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const difference = end - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
          total: difference,
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
        refreshAccess();
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [endDate, isTemporary, refreshToken, login, navigate]);

  if (!hasAccess || !isTemporary) return null;

  return (
    <div
      className={cn(
        "w-full flex items-center justify-center gap-2 py-2 px-3 rounded-md font-semibold text-sm",
        timeLeft.total <= 0
          ? "bg-destructive/15 text-destructive dark:bg-destructive/25"
          : "bg-primary/15 text-primary dark:bg-primary/25",
      )}
    >
      <Clock className="size-4" />
      <span className="text-[13px]">
        {timeLeft.total <= 0
          ? "Access expired"
          : `${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`}
      </span>
    </div>
  );
};

export default AccessTimer;
